// Sentry SDK init for the teacher app. Loaded from index.tsx BEFORE
// ReactDOM.createRoot so the auto-instrumentation can hook the React render
// + react-router navigation events.
//
// Privacy guard rails baked in:
//   - Replay disabled by default; session replay only fires on errors
//     (replaysOnErrorSampleRate: 1.0) so we catch the moments that matter
//     without recording every parent-name + roll-no on every screen.
//   - `maskAllText: true` + `blockAllMedia: true` even on the error
//     replays — names, marks, doubt content, and selfies stay private.
//   - PII scrubber in `beforeSend`: drops email/name/phone if the SDK
//     ever picks them up.
//   - Rate limited at 10 events / minute / browser, capped via a token
//     bucket so a runaway client loop can't shred the org quota.
//
// Env vars (Vite injects them at build time from .env / .env.production):
//   VITE_SENTRY_DSN  = https://<key>@oXXX.ingest.sentry.io/<project>
//                       (omit to disable; Sentry.init() becomes a no-op)
//   VITE_GIT_SHA     = release identifier (deploy.sh exports this; vite
//                       bakes the value into the bundle)
//   VITE_SENTRY_ENV  = production | staging | dev  (defaults to MODE)

import * as Sentry from "@sentry/react";

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const RELEASE = import.meta.env.VITE_GIT_SHA as string | undefined;
const ENV = (import.meta.env.VITE_SENTRY_ENV as string | undefined) || import.meta.env.MODE;

// In-process token bucket — see backend instrument.ts for the same shape.
// A teacher hammering on a broken page shouldn't shred the daily quota.
const RATE_LIMIT_PER_MINUTE = 10;
let tokens = RATE_LIMIT_PER_MINUTE;
let lastRefill = Date.now();
function takeToken(): boolean {
  const now = Date.now();
  const refill = ((now - lastRefill) / 60_000) * RATE_LIMIT_PER_MINUTE;
  tokens = Math.min(RATE_LIMIT_PER_MINUTE, tokens + refill);
  lastRefill = now;
  if (tokens < 1) return false;
  tokens -= 1;
  return true;
}

export function initSentry(): void {
  if (!DSN) {
    console.log("[sentry] teacher monitoring OFF (set VITE_SENTRY_DSN to enable)");
    return;
  }
  Sentry.init({
    dsn: DSN,
    environment: ENV,
    release: RELEASE,
    // 10% trace sample — at a school's traffic this is plenty.
    tracesSampleRate: ENV === "production" ? 0.1 : 1.0,
    // Session replay: never fire proactively (cost + privacy), but capture
    // the 30s window leading up to any error. 100% sample on errors so we
    // never miss the trigger event.
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Privacy — recording is for debugging only, not auditing the user.
        maskAllText: true,
        blockAllMedia: true,
        // Mask form inputs by default; we don't want a leaked complaint
        // body or doubt message to land in a replay.
        maskAllInputs: true,
      }),
    ],
    // ── PII scrubber ──────────────────────────────────────────────────
    beforeSend(event) {
      if (!takeToken()) return null;
      if (event.user) {
        delete event.user.email;
        delete event.user.username;
        delete event.user.ip_address;
        delete (event.user as any).name;
      }
      // The breadcrumb trail is the most useful debugging artefact; strip
      // values from user-typed inputs (ui.input category) so a teacher's
      // complaint draft doesn't end up on a third-party server.
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((b) =>
          b.category === "ui.input"
            ? { ...b, message: "[redacted-input]" }
            : b,
        );
      }
      return event;
    },
    beforeSendTransaction(tx) {
      if (!takeToken()) return null;
      const name = tx.transaction || "";
      // Doubt routes carry conversation content. Drop trace
      // payloads from these (server-side already drops the same — defense
      // in depth).
      if (name.includes("/doubts/")) return null;
      return tx;
    },
  });
  console.log(`[sentry] teacher monitoring on  env=${ENV}  release=${RELEASE || "(unset)"}`);
}

// Re-export the React error boundary so App.tsx can wrap the tree without
// importing the full SDK. The boundary surfaces a simple fallback UI on
// any uncaught React render error AND forwards the error to Sentry.
export const SentryErrorBoundary = Sentry.ErrorBoundary;
